import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { Budget } from './BudgetItem';
import { createTransaction } from '../api/budgets';

function NativeBudgetSelect({ 
  budgets, 
  selectedBudget, 
  onSelect 
}: { 
  budgets: Budget[]; 
  selectedBudget: Budget; 
  onSelect: (budget: Budget) => void; 
}) {
  return (
    <div className="relative flex-1">
      <select
        value={selectedBudget.id}
        onChange={(e) => {
          const budget = budgets.find(b => b.id === e.target.value);
          if (budget) onSelect(budget);
        }}
        className="w-full appearance-none bg-gray-200 rounded-full px-6 py-2.5 text-base font-semibold text-gray-800 focus:outline-none select-none text-center cursor-pointer"
        style={{
          backgroundImage: 'none',
          paddingRight: '2.5rem',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          touchAction: 'manipulation'
        }}
      >
        {budgets.map(budget => (
          <option key={budget.id} value={budget.id} className="text-left px-4">
            {budget.name}
          </option>
        ))}
      </select>
      <ChevronDownIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none" />
    </div>
  );
}

export function QuickAddOverlay({
  budgets,
  initialBudget,
  onClose,
  onTransactionCreated,
  transactionType = 'expense',
  selectedYear,
  selectedMonth,
}: {
  budgets: Budget[];
  initialBudget: Budget;
  onClose: () => void;
  onTransactionCreated: () => void;
  transactionType?: 'expense' | 'savings';
  selectedYear?: number;
  selectedMonth?: number;
}) {
  // Initialize date with selected year/month if provided, otherwise use current date
  const getInitialDate = () => {
    if (selectedYear !== undefined && selectedMonth !== undefined) {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();
      
      // If the selected month is the current month, use today's date
      if (selectedYear === currentYear && selectedMonth === currentMonth) {
        return format(now, 'yyyy-MM-dd');
      }
      
      // Otherwise, use the first day of the selected month
      const date = new Date(selectedYear, selectedMonth, 1);
      return format(date, 'yyyy-MM-dd');
    }
    return format(new Date(), 'yyyy-MM-dd');
  };

  // Check if this is a savings budget
  const isSavingsBudget = transactionType === 'savings' || initialBudget.budgetType === 'Savings';

  const [amount, setAmount] = useState('');
  const [note, setNote] = useState(isSavingsBudget ? initialBudget.name : '');
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [date, setDate] = useState(getInitialDate());
  const [selectedBudget, setSelectedBudget] = useState(initialBudget);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update note when selected budget changes (for savings budgets only)
  useEffect(() => {
    const isSelectedBudgetSavings = transactionType === 'savings' || selectedBudget.budgetType === 'Savings';
    if (isSelectedBudgetSavings) {
      setNote(selectedBudget.name + " Savings");
    }
  }, [selectedBudget, transactionType]);

  // Number pad logic
  const handleNum = (val: string) => {
    if (val === '.' && amount.includes('.')) return;
    setAmount(prev => (prev === '0' && val !== '.' ? val : prev + val));
  };
  const handleBackspace = () => setAmount(prev => prev.slice(0, -1));

  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0 || !note.trim()) return;
    
    try {
      setIsSubmitting(true);
      
      // Create the datetime by combining the selected date with current time
      // Use a more reliable approach to avoid timezone issues
      const now = new Date();
      const selectedDateTime = new Date(date + 'T' + now.toTimeString().split(' ')[0]);
      
      await createTransaction({
        budget: selectedBudget.id!,
        amount: parseFloat(amount),
        datetime: selectedDateTime.toISOString(),
        note: note.trim(),
        transactionType
      });
      
      // Close overlay and refresh data
      onClose();
      onTransactionCreated();
    } catch (error) {
      console.error('Failed to create transaction:', error);
      // You could add error handling UI here
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col w-full max-w-4xl mx-auto">
      {/* Top bar */}
      <div className="flex items-center justify-between py-4 px-8 border-b border-gray-200">
        <span className="text-lg font-semibold text-gray-900 pl-2">Add Transaction</span>
        <button 
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors ml-4" 
          onClick={onClose} 
          aria-label="Close"
        >
          <XMarkIcon className="w-6 h-6 text-gray-600" />
        </button>
      </div>
      {/* Budget and Date Inputs */}
      <div className="flex items-center gap-3 px-8 pt-4">
        <NativeBudgetSelect 
          budgets={budgets}
          selectedBudget={selectedBudget}
          onSelect={setSelectedBudget}
        />
        <input
          type="date"
          className="bg-gray-200 rounded-full px-6 py-2.5 text-base font-semibold text-gray-800 focus:outline-none select-none border-0 w-[170px]"
          value={date}
          onChange={e => setDate(e.target.value)}
        />
      </div>
      {/* Amount */}
      <div className="flex flex-col items-center mt-8 mb-2">
        <div className="text-gray-400 text-sm mb-1">Amount</div>
        <div className="text-4xl font-semibold tracking-wider">${amount || '0'}</div>
      </div>
      {/* Note */}
      <div className="flex flex-col items-center mb-2">
        {showNoteInput ? (
          <input
            className="w-11/12 rounded-lg border border-gray-200 p-2 text-base bg-white mb-2"
            placeholder="Add note (required)..."
            value={note}
            onChange={e => setNote(e.target.value)}
            onBlur={() => setShowNoteInput(false)}
            autoFocus
          />
        ) : (
          <div
            className={`text-base mb-2 cursor-pointer ${note ? 'text-gray-800' : 'text-gray-400'}`}
            onClick={() => setShowNoteInput(true)}
          >
            {note ? note : 'Add note...'}
          </div>
        )}
      </div>
      {/* Number pad */}
      <div className="px-8 mt-2 mb-8">
        <div className="grid grid-cols-3 gap-4 w-full">
          {Array.from('123456789').map(n => (
            <button
              key={n}
              className="text-2xl font-semibold bg-gray-100 rounded-full h-20 w-full flex items-center justify-center active:scale-95 transition"
              onClick={() => handleNum(n)}
            >
              {n}
            </button>
          ))}
          <button
            className="text-2xl font-semibold bg-gray-100 rounded-full h-20 w-full flex items-center justify-center active:scale-95 transition"
            onClick={() => handleNum('.')}
          >
            .
          </button>
          <button
            className="text-2xl font-semibold bg-gray-100 rounded-full h-20 w-full flex items-center justify-center active:scale-95 transition"
            onClick={() => handleNum('0')}
          >
            0
          </button>
          <button
            className="text-2xl font-semibold bg-gray-100 rounded-full h-20 w-full flex items-center justify-center active:scale-95 transition"
            onClick={handleBackspace}
          >
            ←
          </button>
        </div>
      </div>
      {/* Submit button */}
      <div className="px-8 mb-8">
        <button
          className={`w-full py-4 rounded-full text-white text-2xl font-semibold flex items-center justify-center active:scale-95 transition ${
            isSubmitting || !amount || parseFloat(amount) <= 0 || !note.trim()
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-[#22C55E]'
          }`}
          onClick={handleSubmit}
          disabled={isSubmitting || !amount || parseFloat(amount) <= 0 || !note.trim()}
          aria-label="Add transaction"
        >
          {isSubmitting ? 'Adding...' : '✓'}
        </button>
      </div>
    </div>
  );
} 